import React, {useState} from "react";
import {Link, useSearchParams} from "react-router-dom";
import {trackOrder} from "../api";
import "./TrackOrder.css";

// Order lifecycle before Delhivery takes over
const ORDER_STEPS = ["placed", "confirmed", "shipped", "delivered"];

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function OrderProgress({status}) {
  if (status === "cancelled")
    return <p className="track-cancelled">This order was cancelled.</p>;

  const current = ORDER_STEPS.indexOf(status);
  return (
    <ol className="track-steps">
      {ORDER_STEPS.map((step, i) => (
        <li
          key={step}
          className={`track-step ${i <= current ? "done" : ""} ${i === current ? "current" : ""}`}
        >
          <span className="track-step-dot" />
          <span className="track-step-label">{step}</span>
        </li>
      ))}
    </ol>
  );
}

function ShipmentDetails({shipment}) {
  const delivered = shipment.statusType === "DL";
  return (
    <div className="track-shipment">
      <div className="track-status-card">
        <div className="track-status-label">Current status</div>
        <div className="track-status-value">{shipment.status || "—"}</div>
        {shipment.instructions && (
          <div className="track-status-note">{shipment.instructions}</div>
        )}
        <div className="track-status-meta">
          {shipment.statusLocation && <span>📍 {shipment.statusLocation}</span>}
          {shipment.statusDateTime && (
            <span>{formatDateTime(shipment.statusDateTime)}</span>
          )}
        </div>
        {!delivered && shipment.expectedDeliveryDate && (
          <div className="track-eta">
            Expected delivery:{" "}
            <strong>{formatDate(shipment.expectedDeliveryDate)}</strong>
          </div>
        )}
        {delivered && shipment.deliveredAt && (
          <div className="track-eta">
            Delivered on <strong>{formatDate(shipment.deliveredAt)}</strong>
          </div>
        )}
      </div>

      {shipment.scans.length > 0 && (
        <>
          <h3 className="track-section-title">Shipment history</h3>
          <ul className="track-timeline">
            {shipment.scans.map((scan, i) => (
              <li key={i} className={i === 0 ? "latest" : ""}>
                <div className="track-scan-title">{scan.instructions || scan.status}</div>
                <div className="track-scan-meta">
                  {scan.location && <span>{scan.location}</span>}
                  <span>{formatDateTime(scan.dateTime)}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function TrackOrder() {
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(
    searchParams.get("order") || "",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const {data} = await trackOrder(orderNumber.trim());
      setResult(data);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Could not fetch tracking details",
      );
    } finally {
      setLoading(false);
    }
  }

  const order = result?.order;

  return (
    <div className="track-page">
      <div className="track-card">
        <h1>Track your order</h1>
        <p className="track-sub">
          Enter the order number from your confirmation message.
        </p>

        <form className="track-form" onSubmit={handleSubmit}>
          <label>
            Order number
            <input
              type="text"
              required
              placeholder="ORD-00001"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
            />
          </label>
          <button type="submit" className="btn-continue" disabled={loading}>
            {loading ? "Tracking…" : "Track order"}
          </button>
        </form>

        {error && <p className="track-error">{error}</p>}

        {order && (
          <div className="track-result">
            <div className="track-order-head">
              <div>
                <div className="track-order-num">Order #{order.orderNumber}</div>
                {order.placedAt && (
                  <div className="track-order-meta">
                    Placed {formatDate(order.placedAt)} · Delivering to{" "}
                    {order.city}, {order.state}
                  </div>
                )}
                {!order.placedAt && result.shipment?.destination && (
                  <div className="track-order-meta">
                    Delivering to {result.shipment.destination}
                  </div>
                )}
              </div>
              {order.waybill && (
                <div className="track-awb">
                  AWB <strong>{order.waybill}</strong>
                </div>
              )}
            </div>

            {order.orderStatus && <OrderProgress status={order.orderStatus} />}

            {result.shipment ? (
              <ShipmentDetails shipment={result.shipment} />
            ) : result.trackingError ? (
              <p className="track-info">
                Live courier tracking is temporarily unavailable. Please try
                again in a few minutes.
              </p>
            ) : order.waybill || order.orderStatus === "shipped" ? (
              <p className="track-info">
                Your parcel has been handed to Delhivery. Tracking updates
                will appear here once the courier scans it.
              </p>
            ) : (
              order.orderStatus !== "cancelled" && (
                <p className="track-info">
                  Your order is being prepared. Tracking details will appear
                  here once it ships.
                </p>
              )
            )}
          </div>
        )}

        <p className="track-help">
          Need help? <Link to="/contact">Contact us</Link>
        </p>
      </div>
    </div>
  );
}
